import { ExportFileFormat } from '../../../export-task.enum';
import { ProcessorPropertyExcelTypes } from '../processor-types.enums';
import { ExportPropertyTypeTransformParams } from '../processor-types.interface';
import { ExcelCurrency } from './excel-types.enums';
import { isValid, parseISO } from 'date-fns';

/**
 * Converts a JavaScript Date to Excel serial date number using LOCAL timezone.
 * Excel dates are stored as days since December 31, 1899 (day 1 = Jan 1, 1900).
 * Note: Excel incorrectly treats 1900 as a leap year, so we account for that.
 */
function toExcelSerialDate(date: Date): number {
  // Use local date/time components
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Calculate days since Excel epoch using UTC to avoid historical timezone issues
  const localDateUTC = Date.UTC(year, month, day);
  const excelEpochUTC = Date.UTC(1899, 11, 31);
  const msPerDay = 24 * 60 * 60 * 1000;

  let days = (localDateUTC - excelEpochUTC) / msPerDay;

  // Excel bug: treats 1900 as leap year, add 1 day for dates after Feb 28, 1900
  if (days > 59) {
    days += 1;
  }

  // Add time fraction for hours/minutes/seconds
  const timeFraction = (hours * 3600 + minutes * 60 + seconds) / 86400;

  return days + timeFraction;
}

/**
 * Parses various date formats into a Date object.
 * Supports: ISO strings, Date objects, and numeric timestamps.
 */
function parseDate(target: string | number | Date): Date | null {
  if (target === null || target === undefined || target === '' || target === '??') {
    return null;
  }

  if (target instanceof Date) {
    return isValid(target) ? target : null;
  }

  if (typeof target === 'number') {
    const date = new Date(target);
    return isValid(date) ? date : null;
  }

  if (typeof target === 'string') {
    // Try ISO format first (most common from APIs)
    const isoDate = parseISO(target);
    if (isValid(isoDate)) return isoDate;

    // Try native Date parsing as fallback
    const nativeDate = new Date(target);
    if (isValid(nativeDate)) return nativeDate;
  }

  return null;
}

export interface DateTypeOptions {
  format?: string;
}

export const image = (target: StringConstructor): ExportPropertyTypeTransformParams => ({
  __for_processor: true,
  formats: [ExportFileFormat.Excel],
  type: ProcessorPropertyExcelTypes.Hyperlink,
  transform: () => ({
    __post_transform: 'image',
    value: target,
  }),
  fallback: target,
});

export const hyperlink = (target: string, title?: string): ExportPropertyTypeTransformParams => ({
  __for_processor: true,
  formats: [ExportFileFormat.Excel],
  type: ProcessorPropertyExcelTypes.Hyperlink,
  transform: () => ({ formula: `HYPERLINK(${[target, title].filter((s) => s).map((s) => `"${s}"`).join(', ')})` }),
  fallback: target,
});

export const percentage = (target: string | number): ExportPropertyTypeTransformParams => ({
  __for_processor: true,
  formats: [ExportFileFormat.Excel],
  type: ProcessorPropertyExcelTypes.Percentage,
  transform: () => {
    const num = Number(target);
    if (isNaN(num)) return null;
    // Format to be applied at postprocess
    return { v: num, format: '0.00%' };
  },
  fallback: target,
});

/**
 * Date type helper for Excel export.
 * Converts dates to Excel serial numbers with date formatting.
 * Default format: DD/MM/YYYY
 */
export const date = (
  target: string | number | Date,
  options?: DateTypeOptions
): ExportPropertyTypeTransformParams => ({
  __for_processor: true,
  formats: [ExportFileFormat.Excel],
  type: ProcessorPropertyExcelTypes.Date,
  transform: () => {
    const parsedDate = parseDate(target);
    if (!parsedDate) return null;

    const serialDate = toExcelSerialDate(parsedDate);
    const format = options?.format || 'DD/MM/YYYY';

    return { v: serialDate, format };
  },
  fallback: target,
});

/**
 * DateTime type helper for Excel export.
 * Converts dates to Excel serial numbers with datetime formatting.
 * Default format: DD/MM/YYYY HH:MM
 */
export const datetime = (
  target: string | number | Date,
  options?: DateTypeOptions
): ExportPropertyTypeTransformParams => ({
  __for_processor: true,
  formats: [ExportFileFormat.Excel],
  type: ProcessorPropertyExcelTypes.DateTime,
  transform: () => {
    const parsedDate = parseDate(target);
    if (!parsedDate) return null;

    const serialDate = toExcelSerialDate(parsedDate);
    const format = options?.format || 'DD/MM/YYYY HH:MM';

    return { v: serialDate, format };
  },
  fallback: target,
});
